import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  dropinApi,
  isApiError,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import { asMoney } from "@bw/shared/types";
import type {
  DropInCancellationRequest,
  DropInCancellationResponse,
  DropInSessionResponse,
} from "@bw/shared/types/generated";
import { formatMoney, parseDecimal } from "@bw/shared/utils";

const UNAVAILABLE_COPY: Record<string, string> = {
  pending_cancellation_disabled:
    "This facility does not allow pending check-ins to be cancelled.",
  already_started:
    "This session already started and is outside its cancellation window.",
  no_show: "The staff validation window ended and this check-in is a no-show.",
  session_not_live: "This drop-in session is no longer active.",
  cancellation_recovery_pending:
    "A previous cancellation was interrupted. Continue it to finish reconciling this admission.",
};

export interface StaffDropInCancellationTarget {
  session: DropInSessionResponse;
  safePlayerLabel: string;
}

function deadlineLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const deadline = new Date(value);
  if (Number.isNaN(deadline.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(deadline);
}

export function StaffDropInCancellationDialog({
  facilityId,
  target,
  canCancel,
  onCancelled,
  onReconcile,
  onClose,
  onCloseAutoFocus,
}: {
  facilityId: string;
  target: StaffDropInCancellationTarget;
  canCancel: boolean;
  onCancelled: (result: DropInCancellationResponse) => Promise<void>;
  onReconcile: () => Promise<void>;
  onClose: () => void;
  onCloseAutoFocus?: () => void;
}) {
  const [reason, setReason] = useState("");
  const [refundInput, setRefundInput] = useState("");
  const [commandError, setCommandError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [exactRetryRequired, setExactRetryRequired] = useState(false);
  const [persistedRecoveryDetected, setPersistedRecoveryDetected] =
    useState(false);
  const [commandPermissionLost, setCommandPermissionLost] = useState(false);
  const attemptRef = useRef<DropInCancellationRequest | null>(null);
  const inFlightRef = useRef(false);
  const canCancelRef = useRef(canCancel);
  const authorizationLossReconciledRef = useRef(false);
  const dismissalButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(onCloseAutoFocus);
  restoreFocusRef.current = onCloseAutoFocus;
  const decisionQuery = useQuery({
    queryKey: [
      "drop-in",
      "cancellation",
      facilityId,
      target.session.session_id,
    ],
    queryFn: () =>
      dropinApi.getCancellationDecision(facilityId, target.session.session_id),
    enabled: canCancel && !commandPermissionLost,
    staleTime: 0,
    retry: false,
  });
  const decision = decisionQuery.data;
  const decisionPermissionLost =
    isApiError(decisionQuery.error) && decisionQuery.error.status === 403;
  const permissionLost =
    !canCancel || commandPermissionLost || decisionPermissionLost;
  const cancellationActionAllowed = !permissionLost;
  const persistedRecovery =
    persistedRecoveryDetected ||
    target.session.cancellation_recovery_pending === true ||
    decision?.unavailable_reason === "cancellation_recovery_pending";
  const deadline = deadlineLabel(decision?.cancellation_deadline);

  useEffect(() => {
    canCancelRef.current = canCancel;
  }, [canCancel]);

  useEffect(
    () => () => {
      queueMicrotask(() => restoreFocusRef.current?.());
    },
    [],
  );

  useEffect(() => {
    if (!permissionLost || authorizationLossReconciledRef.current) return;
    authorizationLossReconciledRef.current = true;
    attemptRef.current = null;
    void onReconcile();
  }, [onReconcile, permissionLost]);

  useEffect(() => {
    if (!decision || attemptRef.current) return;
    setRefundInput(String(decision.refund_target_amount));
  }, [decision]);

  const closeLocked =
    cancellationActionAllowed && (cancelling || exactRetryRequired);
  const dismissalLabel = permissionLost
    ? "Close"
    : persistedRecovery
      ? decision?.recovery_pending
        ? "Finish later"
        : "Close"
      : "Keep admission";

  function close() {
    if (closeLocked) return;
    onClose();
  }

  async function cancelAdmission() {
    if (
      !cancellationActionAllowed ||
      !decision?.allowed ||
      inFlightRef.current ||
      decisionQuery.isFetching
    ) {
      return;
    }

    let request = attemptRef.current;
    if (!request) {
      const trimmedReason = reason.trim();
      const policyRefund = parseDecimal(decision.refund_target_amount);
      const amountPaid = parseDecimal(decision.amount_paid);
      const enteredRefund = parseDecimal(refundInput);
      let refundOverride: ReturnType<typeof asMoney> | undefined;

      if (decision.override_available) {
        if (enteredRefund === null || enteredRefund < 0) {
          setCommandError("Enter a valid refund target.");
          return;
        }
        if (amountPaid !== null && enteredRefund > amountPaid) {
          setCommandError(
            `Refund target cannot exceed the amount paid (${formatMoney(
              decision.amount_paid,
              decision.currency,
            )}).`,
          );
          return;
        }
        const deviates =
          policyRefund === null ||
          Math.abs(enteredRefund - policyRefund) > 0.0001;
        if (deviates) {
          if (!trimmedReason) {
            setCommandError(
              "A reason is required when you change the refund target.",
            );
            return;
          }
          refundOverride = asMoney(enteredRefund.toFixed(2));
          if (!refundOverride) {
            setCommandError("Enter a valid refund target.");
            return;
          }
        }
      }

      request = {
        idempotency_key: globalThis.crypto.randomUUID(),
        ...(trimmedReason ? { reason: trimmedReason } : {}),
        ...(refundOverride
          ? { refund_target_amount_override: refundOverride }
          : {}),
      };
      attemptRef.current = request;
      setExactRetryRequired(false);
    }

    inFlightRef.current = true;
    setCancelling(true);
    setCommandError(null);
    try {
      const result = await dropinApi.cancelSession(
        facilityId,
        target.session.session_id,
        request,
      );
      await onCancelled(result);
    } catch (error) {
      if (
        !canCancelRef.current ||
        (isApiError(error) && error.status === 403)
      ) {
        attemptRef.current = null;
        setExactRetryRequired(false);
        setCommandPermissionLost(true);
      } else if (
        isApiError(error) &&
        error.code === "DROPIN_CANCELLATION_RECOVERY_PENDING"
      ) {
        attemptRef.current = null;
        setExactRetryRequired(false);
        setPersistedRecoveryDetected(true);
        setCommandError(null);
        void Promise.allSettled([decisionQuery.refetch(), onReconcile()]);
      } else if (requiresExactCancellationRetry(error)) {
        setExactRetryRequired(true);
        setCommandError(
          isApiError(error)
            ? error.message
            : "The response was interrupted. Retry the exact same cancellation request.",
        );
      } else {
        attemptRef.current = null;
        setExactRetryRequired(false);
        setCommandError(
          isApiError(error)
            ? error.message
            : "Could not cancel this admission. Review the latest decision and try again.",
        );
        if (isApiError(error) && error.status === 409) {
          await decisionQuery.refetch();
        }
      }
    } finally {
      inFlightRef.current = false;
      setCancelling(false);
    }
  }

  async function recoverAdmissionCancellation() {
    if (
      !cancellationActionAllowed ||
      !decision?.recovery_pending ||
      inFlightRef.current ||
      decisionQuery.isFetching
    ) {
      return;
    }

    inFlightRef.current = true;
    setCancelling(true);
    setCommandError(null);
    try {
      const result = await dropinApi.recoverCancellation(
        facilityId,
        target.session.session_id,
      );
      await onCancelled(result);
    } catch (error) {
      if (
        !canCancelRef.current ||
        (isApiError(error) && error.status === 403)
      ) {
        setCommandPermissionLost(true);
        setCommandError(null);
      } else {
        setCommandError(
          isApiError(error)
            ? error.message
            : "Could not finish reconciling this cancellation. Try continuing it again.",
        );
      }
    } finally {
      inFlightRef.current = false;
      setCancelling(false);
    }
  }

  return (
    <Dialog
      open
      onClose={close}
      disableRestoreFocus
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { maxHeight: "calc(100dvh - 2rem)" } },
      }}
    >
      <DialogTitle>Cancel drop-in session</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Review the facility policy outcome before ending this admission.
          </Typography>

          <Box
            component="dl"
            sx={{
              m: 0,
              display: "grid",
              gridTemplateColumns: "auto minmax(0, 1fr)",
              columnGap: 2,
              rowGap: 0.5,
            }}
          >
            <Typography component="dt" variant="body2" color="text.secondary">
              Player
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{ m: 0, overflowWrap: "anywhere", fontWeight: 600 }}
            >
              {target.safePlayerLabel}
            </Typography>
            <Typography component="dt" variant="body2" color="text.secondary">
              Payment
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{ m: 0, fontWeight: 600 }}
            >
              {target.session.payment_method.charAt(0).toUpperCase() +
                target.session.payment_method.slice(1)}
            </Typography>
          </Box>

          {permissionLost ? (
            <Alert severity="error">
              Your permission to manage this cancellation changed. The
              checked-in roster is being refreshed.
            </Alert>
          ) : decisionQuery.isPending || decisionQuery.isFetching ? (
            <Typography variant="body2" color="text.secondary" role="status">
              Loading cancellation decision…
            </Typography>
          ) : decisionQuery.isError || !decision ? (
            <Alert
              severity="error"
              action={
                <Button
                  type="button"
                  size="small"
                  color="inherit"
                  onClick={() => void decisionQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Could not load the current cancellation decision.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Policy version {decision.accepted_policy_version}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {decision.accepted_policy_summary}
                </Typography>
              </Box>

              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr)",
                  columnGap: 2,
                  rowGap: 0.5,
                }}
              >
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Amount paid
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {formatMoney(decision.amount_paid, decision.currency)}
                </Typography>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Policy refund target
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {formatMoney(
                    decision.refund_target_amount,
                    decision.currency,
                  )}
                </Typography>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Visit pass
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, fontWeight: 600 }}
                >
                  {decision.restore_package_visit
                    ? "1 visit will be restored"
                    : "No visit will be restored"}
                </Typography>
              </Box>
              {Number(decision.refund_target_amount) > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Existing refunds count toward this total.
                </Typography>
              ) : null}

              {deadline ? (
                <Typography variant="caption" color="text.secondary">
                  Available until{" "}
                  <time dateTime={decision.cancellation_deadline ?? ""}>
                    {deadline}
                  </time>
                  .
                </Typography>
              ) : null}

              {!decision.allowed ? (
                <Alert severity="error">
                  {decision.unavailable_reason ===
                    "cancellation_recovery_pending" &&
                  !decision.recovery_pending
                    ? "This cancellation requires another authorized operator to finish it."
                    : (UNAVAILABLE_COPY[decision.unavailable_reason ?? ""] ??
                      "This session cannot be cancelled.")}
                </Alert>
              ) : null}

              {decision.allowed && decision.override_available ? (
                <TextField
                  id="drop-in-refund-amount"
                  label="Refund target"
                  type="number"
                  value={refundInput}
                  disabled={closeLocked}
                  onChange={(event) => {
                    setRefundInput(event.target.value);
                    setCommandError(null);
                    attemptRef.current = null;
                  }}
                  helperText="Changing the policy target requires a reason."
                  slotProps={{
                    htmlInput: {
                      inputMode: "decimal",
                      min: 0,
                      max: String(decision.amount_paid),
                      step: "0.01",
                    },
                  }}
                />
              ) : null}

              {decision.allowed ? (
                <TextField
                  id="drop-in-cancellation-reason"
                  label="Reason / note"
                  multiline
                  rows={3}
                  value={reason}
                  disabled={closeLocked}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setCommandError(null);
                    attemptRef.current = null;
                  }}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />
              ) : null}
            </Stack>
          )}

          {commandError && !permissionLost ? (
            <Alert severity="error">{commandError}</Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          ref={dismissalButtonRef}
          type="button"
          size="small"
          variant="outlined"
          autoFocus
          onClick={close}
          disabled={closeLocked}
        >
          {dismissalLabel}
        </Button>
        {cancellationActionAllowed &&
        !persistedRecovery &&
        decision?.allowed &&
        !decisionQuery.isError ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            onClick={() => void cancelAdmission()}
            disabled={cancelling || decisionQuery.isFetching}
          >
            {cancelling
              ? "Cancelling…"
              : exactRetryRequired
                ? "Retry exact cancellation"
                : "Confirm cancellation"}
          </Button>
        ) : null}
        {cancellationActionAllowed &&
        decision?.recovery_pending &&
        !decisionQuery.isError ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            onClick={() => void recoverAdmissionCancellation()}
            disabled={cancelling || decisionQuery.isFetching}
          >
            {cancelling ? "Continuing…" : "Continue cancellation"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
